import React, { useState, useEffect, useCallback } from 'react';
import {
  Drawer, Descriptions, Steps, Tabs, Table, Tag, List, Empty, Spin,
  Badge, Collapse, Button, Space, message,
} from 'antd';
import { BugOutlined, ReloadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type {
  ModuleDetail,
  SubmoduleItem,
  EndpointEntry,
  SolidDetail,
  SolidScoreEntry,
} from '../../../services/devApi';
import { bugReportApi } from '../../../services/bugReportApi';
import type { BugReport, Annotation } from '../../../components/BugReport/types';

interface ModuleDetailDrawerProps {
  visible: boolean;
  module: ModuleDetail | null;
  loading: boolean;
  onClose: () => void;
}

const SOLID_LABELS: Record<string, string> = {
  S: 'S 單一職責',
  O: 'O 開放封閉',
  L: 'L 里氏替換',
  I: 'I 介面隔離',
  D: 'D 依賴反轉',
  BR: 'BR 商業規則',
  TC: 'TC 測試覆蓋',
  Security: '安全性',
  Traps: '實作陷阱',
};

const SOLID_KEYS = ['S', 'O', 'L', 'I', 'D', 'BR', 'TC', 'Security', 'Traps'];

const BUG_STATUS_CONFIG: Record<string, { color: string; label: string }> = {
  open: { color: 'red', label: 'Open' },
  in_progress: { color: 'orange', label: 'In Progress' },
  resolved: { color: 'green', label: 'Resolved' },
  closed: { color: 'default', label: 'Closed' },
};

function getStatusTag(status: string): React.ReactNode {
  const map: Record<string, { text: string; color: string }> = {
    '--': { text: '--', color: 'default' },
    wip: { text: 'WIP', color: 'processing' },
    done: { text: 'DONE', color: 'success' },
    bug: { text: 'BUG', color: 'error' },
    blocked: { text: 'BLOCKED', color: 'warning' },
  };
  const cfg = map[status] || map['--'];
  return <Tag color={cfg.color}>{cfg.text}</Tag>;
}

function stepStatus(status: string): 'finish' | 'process' | 'wait' | 'error' {
  if (status === 'done') return 'finish';
  if (status === 'wip') return 'process';
  if (status === 'bug') return 'error';
  return 'wait';
}

function getMethodTagColor(method: string): string {
  const m = method.toUpperCase();
  if (m === 'GET') return 'green';
  if (m === 'POST') return 'blue';
  if (m === 'PUT') return 'orange';
  if (m === 'DELETE') return 'red';
  return 'default';
}

function scoreColor(score: number): string {
  if (score >= 8) return 'text-green-600';
  if (score >= 6) return 'text-yellow-600';
  return 'text-red-600';
}

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '--';
  const d = new Date(dateStr);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${mm}-${dd} ${hh}:${min}`;
}

function renderSolidTable(detail: SolidDetail | null) {
  if (!detail || Object.keys(detail).length === 0) {
    return <Empty description="尚無審查資料" />;
  }

  const dataSource = SOLID_KEYS.map((key) => {
    const entry: SolidScoreEntry = detail[key] || { score: 0, note: '' };
    return { key, label: SOLID_LABELS[key] || key, score: entry.score, note: entry.note };
  });

  const totalScore = dataSource.reduce((sum, r) => sum + r.score, 0);

  const columns: ColumnsType<{ key: string; label: string; score: number; note: string }> = [
    { title: '項目', dataIndex: 'label', key: 'label', width: 130 },
    {
      title: '分數',
      dataIndex: 'score',
      key: 'score',
      width: 80,
      render: (score: number) => (
        <span className={`font-bold ${scoreColor(score)}`}>{score}/10</span>
      ),
    },
    { title: '備註', dataIndex: 'note', key: 'note' },
  ];

  return (
    <>
      <Table
        dataSource={dataSource}
        columns={columns}
        size="small"
        pagination={false}
        rowKey="key"
      />
      <div className="mt-2 text-right font-bold">
        總分：
        <span className={totalScore >= 72 ? 'text-green-600' : 'text-red-600'}>
          {totalScore}/90
        </span>
        {' '}
        {totalScore >= 72 ? (
          <Tag color="success">PASS</Tag>
        ) : (
          <Tag color="error">FAIL</Tag>
        )}
      </div>
    </>
  );
}

// ============================================================
// Bug Report Tab
// ============================================================

const BugReportTab: React.FC<{ formId: string }> = ({ formId }) => {
  const [bugs, setBugs] = useState<BugReport[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);

  const fetchBugs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await bugReportApi.list({ formId, size: 50 });
      setBugs(res.data);
      setTotal(res.total);
    } catch (err) {
      console.error('Fetch bugs error:', err);
    } finally {
      setLoading(false);
    }
  }, [formId]);

  useEffect(() => {
    fetchBugs();
  }, [fetchBugs]);

  const handleStatusChange = async (id: number, newStatus: string) => {
    try {
      await bugReportApi.update(id, { status: newStatus });
      message.success(`Bug #${id} → ${newStatus}`);
      fetchBugs();
    } catch {
      message.error('更新失敗');
    }
  };

  const openCount = bugs.filter(b => b.status === 'open' || b.status === 'in_progress').length;

  const columns: ColumnsType<BugReport> = [
    {
      title: '#',
      dataIndex: 'id',
      key: 'id',
      width: 50,
      render: (id: number) => <span className="font-bold">#{id}</span>,
    },
    {
      title: '狀態',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: string) => {
        const cfg = BUG_STATUS_CONFIG[status] || { color: 'default', label: status };
        return <Tag color={cfg.color}>{cfg.label}</Tag>;
      },
    },
    {
      title: '頁面',
      dataIndex: 'page',
      key: 'page',
      width: 130,
      ellipsis: true,
    },
    {
      title: '標註',
      dataIndex: 'annotations',
      key: 'annotations',
      width: 60,
      render: (annotations: Annotation[]) => <Tag>{annotations?.length || 0}</Tag>,
    },
    {
      title: '回報人',
      dataIndex: 'reportedBy',
      key: 'reportedBy',
      width: 80,
    },
    {
      title: '時間',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 100,
      render: (val: string) => formatDate(val),
    },
    {
      title: '',
      key: 'actions',
      width: 140,
      render: (_: unknown, record: BugReport) => (
        <Space size="small">
          {record.status === 'open' && (
            <Button size="small" onClick={() => handleStatusChange(record.id, 'in_progress')}>
              Start
            </Button>
          )}
          {(record.status === 'open' || record.status === 'in_progress') && (
            <Button size="small" type="primary" onClick={() => handleStatusChange(record.id, 'resolved')}>
              Resolve
            </Button>
          )}
        </Space>
      ),
    },
  ];

  const expandedRowRender = (record: BugReport) => {
    const annotations = record.annotations || [];
    return (
      <div>
        <Collapse
          size="small"
          items={annotations.map((a, i) => ({
            key: i,
            label: (
              <span>
                <Tag color="red">#{i + 1}</Tag>
                {a.label && <Tag color="blue">{a.label}</Tag>}
                <code style={{ fontSize: 11, color: '#8c8c8c' }}>{a.selector}</code>
              </span>
            ),
            children: (
              <div>
                <p style={{ marginBottom: 4 }}>{a.description}</p>
                <span style={{ fontSize: 11, color: '#8c8c8c' }}>
                  &lt;{a.elementTag}&gt; | "{a.elementText}"
                </span>
              </div>
            ),
          }))}
        />
        {record.resolution && (
          <div style={{ marginTop: 8, padding: '8px 12px', background: '#f6ffed', borderRadius: 4, border: '1px solid #b7eb8f' }}>
            <Tag color="green">修復說明</Tag>
            <span>{record.resolution}</span>
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <Space>
          {openCount > 0 ? (
            <Tag color="red">{openCount} 個待處理</Tag>
          ) : total > 0 ? (
            <Tag color="green">全部已處理</Tag>
          ) : null}
          <span className="text-gray-400 text-xs">共 {total} 筆</span>
        </Space>
        <Button size="small" icon={<ReloadOutlined />} onClick={fetchBugs} loading={loading}>
          刷新
        </Button>
      </div>
      {total === 0 && !loading ? (
        <Empty description="尚無 Bug 回報" />
      ) : (
        <Table
          columns={columns}
          dataSource={bugs}
          rowKey="id"
          loading={loading}
          size="small"
          expandable={{ expandedRowRender }}
          pagination={false}
        />
      )}
    </div>
  );
};

// ============================================================
// Main Drawer
// ============================================================

const ModuleDetailDrawer: React.FC<ModuleDetailDrawerProps> = ({
  visible,
  module,
  loading,
  onClose,
}) => {
  const drawerWidth = typeof window !== 'undefined' && window.innerWidth < 768 ? '100%' : 640;

  if (!visible) return null;

  const m = module;

  // Submodule table columns
  const submoduleColumns: ColumnsType<SubmoduleItem> = [
    { title: '子模組 ID', dataIndex: 'submoduleId', key: 'submoduleId', width: 100 },
    { title: '名稱', dataIndex: 'name', key: 'name', width: 120 },
    {
      title: '狀態',
      dataIndex: 'status',
      key: 'status',
      width: 80,
      render: (s: string) => getStatusTag(s),
    },
    {
      title: 'SOLID',
      dataIndex: 'solidScore',
      key: 'solidScore',
      width: 80,
      render: (score: number) =>
        score > 0 ? (
          <span className={`font-bold ${score >= 72 ? 'text-green-600' : 'text-red-600'}`}>
            {score}/90
          </span>
        ) : (
          <span className="text-gray-400">--</span>
        ),
    },
    {
      title: 'E2E',
      dataIndex: 'e2eStatus',
      key: 'e2eStatus',
      width: 80,
      render: (s: string) => getStatusTag(s),
    },
    {
      title: '完成時間',
      dataIndex: 'completedAt',
      key: 'completedAt',
      width: 110,
      render: (d: string | null) => formatDate(d),
    },
  ];

  // Endpoint table columns
  const endpointColumns: ColumnsType<EndpointEntry> = [
    {
      title: 'Method',
      dataIndex: 'method',
      key: 'method',
      width: 80,
      render: (method: string) => <Tag color={getMethodTagColor(method)}>{method}</Tag>,
    },
    { title: 'Path', dataIndex: 'path', key: 'path', width: 200 },
    { title: '說明', dataIndex: 'description', key: 'description' },
  ];

  // Steps items
  const stepsItems = m
    ? [
        {
          title: '後端開發',
          status: stepStatus(m.backendStatus),
          description: m.backendCompletedAt ? formatDate(m.backendCompletedAt) : undefined,
        },
        {
          title: '後端 SOLID 審查',
          status: m.beSolidScore > 0
            ? (m.beSolidScore >= 72 ? 'finish' as const : 'error' as const)
            : 'wait' as const,
          description: m.beSolidScore > 0
            ? `${m.beSolidScore >= 72 ? 'PASS' : 'FAIL'} ${m.beSolidScore}/90`
            : '待審',
        },
        {
          title: '前端開發',
          status: stepStatus(m.frontendStatus),
          description: m.frontendCompletedAt ? formatDate(m.frontendCompletedAt) : undefined,
        },
        {
          title: '前端 SOLID 審查',
          status: m.feSolidScore > 0
            ? (m.feSolidScore >= 72 ? 'finish' as const : 'error' as const)
            : 'wait' as const,
          description: m.feSolidScore > 0
            ? `${m.feSolidScore >= 72 ? 'PASS' : 'FAIL'} ${m.feSolidScore}/90`
            : '待審',
        },
        {
          title: 'E2E 測試',
          status: stepStatus(m.e2eStatus),
          description: m.e2eCompletedAt ? formatDate(m.e2eCompletedAt) : undefined,
        },
      ]
    : [];

  // SOLID tabs
  const hasBeSolid = m?.beSolidDetail && Object.keys(m.beSolidDetail).length > 0;
  const hasFeSolid = m?.feSolidDetail && Object.keys(m.feSolidDetail).length > 0;
  const solidTabItems = [];
  if (hasBeSolid || m?.beSolidScore) {
    solidTabItems.push({
      key: 'be',
      label: '後端 SOLID',
      children: renderSolidTable(m?.beSolidDetail || null),
    });
  }
  if (hasFeSolid || m?.feSolidScore) {
    solidTabItems.push({
      key: 'fe',
      label: '前端 SOLID',
      children: renderSolidTable(m?.feSolidDetail || null),
    });
  }

  // Progress content
  const progressContent = m ? (
    <div className="space-y-6">
      {/* Basic info */}
      <Descriptions column={2} bordered size="small">
        <Descriptions.Item label="FormId">
          <span className="font-mono">{m.formId}</span>
        </Descriptions.Item>
        <Descriptions.Item label="Spec 路徑">{m.specDir || '--'}</Descriptions.Item>
        <Descriptions.Item label="Audit 分數">{m.auditScore || '--'}</Descriptions.Item>
        <Descriptions.Item label="總狀態">{getStatusTag(m.status)}</Descriptions.Item>
      </Descriptions>

      {/* Progress timeline */}
      <Steps
        direction="vertical"
        size="small"
        items={stepsItems}
      />

      {/* SOLID detail */}
      {solidTabItems.length > 0 && (
        <Tabs items={solidTabItems} size="small" />
      )}

      {/* File list */}
      {m.files && m.files.length > 0 && (
        <div>
          <h4 className="font-bold mb-2">檔案清單</h4>
          <List
            size="small"
            bordered
            dataSource={m.files}
            renderItem={(file) => (
              <List.Item>
                <span className="font-mono text-sm">{file.path}</span>
                <span className="text-gray-400 text-xs ml-2">({file.lines} 行)</span>
              </List.Item>
            )}
          />
        </div>
      )}

      {/* API Endpoints */}
      {m.endpoints && m.endpoints.length > 0 && (
        <div>
          <h4 className="font-bold mb-2">API Endpoints</h4>
          <Table<EndpointEntry>
            dataSource={m.endpoints}
            columns={endpointColumns}
            size="small"
            pagination={false}
            rowKey={(_, i) => String(i)}
          />
        </div>
      )}

      {/* Submodules */}
      {m.submodules && m.submodules.length > 0 && (
        <div>
          <h4 className="font-bold mb-2">子模組列表</h4>
          <Table<SubmoduleItem>
            dataSource={m.submodules}
            columns={submoduleColumns}
            size="small"
            pagination={false}
            rowKey="submoduleId"
          />
        </div>
      )}
    </div>
  ) : null;

  // Drawer-level tabs
  const drawerTabs = [
    {
      key: 'progress',
      label: '開發進度',
      children: progressContent,
    },
    {
      key: 'bugs',
      label: (
        <span>
          <BugOutlined style={{ marginRight: 4 }} />
          Bug 回報
        </span>
      ),
      children: m ? <BugReportTab formId={m.formId} /> : null,
    },
  ];

  return (
    <Drawer
      title={m ? `"${m.name || m.formId}" 開發進度` : '模組詳情'}
      placement="right"
      width={drawerWidth}
      open={visible}
      onClose={onClose}
      destroyOnClose
    >
      {loading || !m ? (
        <div className="flex justify-center items-center h-64">
          <Spin size="large" />
        </div>
      ) : (
        <Tabs items={drawerTabs} size="small" />
      )}
    </Drawer>
  );
};

export default ModuleDetailDrawer;
